import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    MapPin, School, Users, ChevronRight, ChevronLeft, 
    Building, Loader2, GraduationCap, Search, X
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// SVG paths for Polish voivodeships
const voivodeshipPaths = {
    'DOLNOŚLĄSKIE': 'M85 220 L120 200 L140 220 L130 250 L95 260 L75 245 Z',
    'KUJAWSKO-POMORSKIE': 'M170 100 L220 90 L240 120 L220 150 L175 145 L160 120 Z',
    'LUBELSKIE': 'M300 180 L350 170 L360 220 L340 270 L290 260 L285 210 Z',
    'LUBUSKIE': 'M50 150 L90 140 L100 180 L80 210 L45 200 L40 165 Z',
    'ŁÓDZKIE': 'M185 170 L230 165 L245 210 L220 245 L180 240 L170 200 Z',
    'MAŁOPOLSKIE': 'M220 280 L280 275 L295 310 L265 340 L215 330 L205 300 Z',
    'MAZOWIECKIE': 'M235 130 L295 125 L310 175 L290 220 L240 215 L225 165 Z',
    'OPOLSKIE': 'M140 250 L175 245 L185 280 L165 305 L130 295 L125 265 Z',
    'PODKARPACKIE': 'M305 275 L360 270 L375 310 L350 345 L300 335 L290 300 Z',
    'PODLASKIE': 'M310 70 L370 60 L385 110 L365 155 L315 145 L300 100 Z',
    'POMORSKIE': 'M140 30 L200 20 L215 65 L190 100 L145 95 L130 55 Z',
    'ŚLĄSKIE': 'M175 280 L220 275 L230 315 L205 345 L165 335 L160 300 Z',
    'ŚWIĘTOKRZYSKIE': 'M245 235 L285 230 L295 265 L275 295 L240 285 L235 255 Z',
    'WARMIŃSKO-MAZURSKIE': 'M230 35 L300 25 L320 70 L295 110 L240 100 L220 60 Z',
    'WIELKOPOLSKIE': 'M100 130 L165 120 L180 170 L155 215 L100 205 L85 160 Z',
    'ZACHODNIOPOMORSKIE': 'M45 40 L130 30 L145 85 L110 125 L50 115 L35 70 Z',
};

export const PolandMap = () => {
    const navigate = useNavigate();
    
    // Navigation state: 'map' -> 'voivodeship' -> 'city' -> 'school'
    const [view, setView] = useState('map');
    const [selectedVoivodeship, setSelectedVoivodeship] = useState(null);
    const [selectedCity, setSelectedCity] = useState(null);
    const [selectedSchool, setSelectedSchool] = useState(null);
    const [hoveredVoivodeship, setHoveredVoivodeship] = useState(null);
    
    // Data
    const [stats, setStats] = useState(null);
    const [cities, setCities] = useState([]);
    const [schools, setSchools] = useState([]);
    const [schoolDetails, setSchoolDetails] = useState(null);
    
    // Loading states
    const [loading, setLoading] = useState(false);
    
    // Search
    const [citySearch, setCitySearch] = useState('');
    const [schoolSearch, setSchoolSearch] = useState('');

    // Load overall stats on mount
    useEffect(() => {
        const fetchStats = async () => {
            try {
                const response = await fetch(`${API_URL}/api/schools/stats`);
                const data = await response.json();
                setStats(data);
            } catch (error) {
                console.error('Stats error:', error);
            }
        };
        fetchStats();
    }, []);

    // Load cities when voivodeship is selected
    useEffect(() => {
        if (view === 'voivodeship' && selectedVoivodeship) {
            const fetchCities = async () => {
                setLoading(true);
                try {
                    const response = await fetch(
                        `${API_URL}/api/schools/cities?voivodeship=${encodeURIComponent(selectedVoivodeship)}&limit=50`
                    );
                    const data = await response.json();
                    setCities(data.cities || []);
                } catch (error) {
                    console.error('Cities error:', error);
                }
                setLoading(false);
            };
            fetchCities();
        }
    }, [view, selectedVoivodeship]);

    // Load schools when city is selected
    useEffect(() => {
        if (view === 'city' && selectedCity) {
            const fetchSchools = async () => {
                setLoading(true);
                try {
                    const response = await fetch(
                        `${API_URL}/api/schools/by-city/${encodeURIComponent(selectedCity)}?limit=50`
                    );
                    const data = await response.json();
                    setSchools(data.schools || []);
                } catch (error) {
                    console.error('Schools error:', error);
                }
                setLoading(false);
            };
            fetchSchools();
        }
    }, [view, selectedCity]);

    // Load school details
    useEffect(() => {
        if (view === 'school' && selectedSchool) {
            const fetchSchoolDetails = async () => {
                setLoading(true);
                try {
                    const response = await fetch(`${API_URL}/api/schools/${selectedSchool.id}`);
                    const data = await response.json();
                    setSchoolDetails(data);
                } catch (error) {
                    console.error('School details error:', error);
                }
                setLoading(false);
            };
            fetchSchoolDetails();
        }
    }, [view, selectedSchool]);

    // Get voivodeship stats
    const getVoivodeshipStats = (name) => {
        if (!stats?.by_voivodeship) return { count: 0 };
        const found = stats.by_voivodeship.find(v => v._id === name);
        return found || { count: 0 };
    };

    // Handle voivodeship click
    const handleVoivodeshipClick = (name) => {
        setSelectedVoivodeship(name);
        setView('voivodeship');
        setCitySearch('');
    };

    // Handle city click
    const handleCityClick = (cityName) => {
        setSelectedCity(cityName);
        setView('city');
        setSchoolSearch('');
    };

    // Handle school click
    const handleSchoolClick = (school) => {
        setSelectedSchool(school);
        setView('school');
    };

    // Go back
    const goBack = () => {
        if (view === 'school') {
            setView('city');
            setSelectedSchool(null);
            setSchoolDetails(null);
        } else if (view === 'city') {
            setView('voivodeship');
            setSelectedCity(null);
            setSchools([]);
        } else if (view === 'voivodeship') {
            setView('map');
            setSelectedVoivodeship(null);
            setCities([]);
        }
    };

    // Filter cities
    const filteredCities = cities.filter(c => 
        c.city?.toLowerCase().includes(citySearch.toLowerCase())
    );

    // Filter schools
    const filteredSchools = schools.filter(s => 
        s.name?.toLowerCase().includes(schoolSearch.toLowerCase())
    );

    // Render map view
    const renderMap = () => (
        <>
            <p className="text-xs text-muted-foreground mb-3 text-center">
                Kliknij na województwo, aby zobaczyć szkoły
            </p>
            
            <TooltipProvider delayDuration={100}>
                <svg viewBox="0 0 410 380" className="w-full h-auto max-h-72">
                    <rect x="0" y="0" width="410" height="380" fill="hsl(var(--muted))" rx="8" />
                    
                    <defs>
                        <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>

                    {Object.entries(voivodeshipPaths).map(([name, path]) => {
                        const voivStats = getVoivodeshipStats(name);
                        const isHovered = hoveredVoivodeship === name;
                        
                        return (
                            <Tooltip key={name}>
                                <TooltipTrigger asChild>
                                    <path
                                        d={path}
                                        fill={isHovered ? 'hsl(var(--accent-green))' : 'hsl(var(--primary))'}
                                        stroke="hsl(var(--background))"
                                        strokeWidth="2"
                                        className="cursor-pointer transition-all duration-200"
                                        style={{
                                            filter: isHovered ? 'url(#glow)' : 'none',
                                        }}
                                        onMouseEnter={() => setHoveredVoivodeship(name)}
                                        onMouseLeave={() => setHoveredVoivodeship(null)}
                                        onClick={() => handleVoivodeshipClick(name)}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Województwo ${name}${voivStats.count ? ` - ${voivStats.count} szkół` : ''}`}
                                        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleVoivodeshipClick(name)}
                                    />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="nk-box p-2 text-xs">
                                    <div className="space-y-1">
                                        <p className="font-semibold text-foreground">{name}</p>
                                        <p className="flex items-center gap-1 text-muted-foreground">
                                            <School className="w-3 h-3" /> {voivStats.count?.toLocaleString()} szkół
                                        </p>
                                    </div>
                                </TooltipContent>
                            </Tooltip>
                        );
                    })}
                </svg>
            </TooltipProvider>

            {/* Stats summary */}
            {stats && (
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded-lg bg-muted/50 text-center">
                        <p className="font-bold text-lg text-primary">{stats.total_schools?.toLocaleString()}</p>
                        <p className="text-muted-foreground">szkół w bazie</p>
                    </div>
                    <div className="p-2 rounded-lg bg-muted/50 text-center">
                        <p className="font-bold text-lg text-accent-green">{stats.total_members?.toLocaleString() || 0}</p>
                        <p className="text-muted-foreground">użytkowników</p>
                    </div>
                </div>
            )}
        </>
    );

    // Render voivodeship view (cities)
    const renderVoivodeship = () => (
        <>
            <div className="flex items-center gap-2 mb-3">
                <Button variant="ghost" size="sm" onClick={goBack} className="p-1 h-7" aria-label="Cofnij do poprzedniego widoku">
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <h3 className="font-semibold text-sm flex-1">{selectedVoivodeship}</h3>
                <Badge variant="secondary" className="text-xs">
                    {cities.length} miast
                </Badge>
            </div>

            {/* Search cities */}
            <div className="relative mb-3">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                <Input
                    value={citySearch}
                    onChange={(e) => setCitySearch(e.target.value)}
                    placeholder="Szukaj miasta..."
                    className="h-8 text-xs pl-7"
                />
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredCities.map((city, idx) => (
                        <button
                            key={`${city.city}-${idx}`}
                            onClick={() => handleCityClick(city.city)}
                            className="w-full flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                        >
                            <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-primary" />
                                <span className="text-sm font-medium">{city.city}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">{city.school_count} szkół</span>
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                        </button>
                    ))}
                    {filteredCities.length === 0 && (
                        <p className="text-center text-sm text-muted-foreground py-4">
                            Nie znaleziono miast
                        </p>
                    )}
                </div>
            )}
        </>
    );

    // Render city view (schools)
    const renderCity = () => (
        <>
            <div className="flex items-center gap-2 mb-3">
                <Button variant="ghost" size="sm" onClick={goBack} className="p-1 h-7" aria-label="Cofnij do poprzedniego widoku">
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex-1">
                    <h3 className="font-semibold text-sm">{selectedCity}</h3>
                    <p className="text-xs text-muted-foreground">{selectedVoivodeship}</p>
                </div>
                <Badge variant="secondary" className="text-xs">
                    {schools.length} szkół
                </Badge>
            </div>

            {/* Search schools */}
            <div className="relative mb-3">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                <Input
                    value={schoolSearch}
                    onChange={(e) => setSchoolSearch(e.target.value)}
                    placeholder="Szukaj szkoły..."
                    className="h-8 text-xs pl-7"
                />
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
            ) : (
                <div className="max-h-64 overflow-y-auto space-y-1">
                    {filteredSchools.map((school) => (
                        <button
                            key={school.id}
                            onClick={() => handleSchoolClick(school)}
                            className="w-full p-2 rounded-lg hover:bg-muted/50 transition-colors text-left"
                        >
                            <div className="flex items-start gap-2">
                                <School className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium leading-tight line-clamp-2">
                                        {school.name}
                                    </p>
                                    <div className="flex items-center gap-2 mt-1">
                                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                            {school.typeCategory || school.type}
                                        </Badge>
                                        {school.members_count > 0 && (
                                            <span className="text-[10px] text-accent-green flex items-center gap-0.5">
                                                <Users className="w-3 h-3" />
                                                {school.members_count}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                            </div>
                        </button>
                    ))}
                    {filteredSchools.length === 0 && (
                        <p className="text-center text-sm text-muted-foreground py-4">
                            Nie znaleziono szkół
                        </p>
                    )}
                </div>
            )}
        </>
    );

    // Render school profile view
    const renderSchool = () => {
        const school = schoolDetails || selectedSchool;
        
        return (
            <>
                <div className="flex items-center gap-2 mb-3">
                    <Button variant="ghost" size="sm" onClick={goBack} className="p-1 h-7">
                        <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <h3 className="font-semibold text-sm flex-1 line-clamp-1">Profil szkoły</h3>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    </div>
                ) : school ? (
                    <div className="space-y-3">
                        {/* School name */}
                        <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
                            <div className="flex items-start gap-2">
                                <School className="w-5 h-5 text-primary mt-0.5" />
                                <div>
                                    <h4 className="font-semibold text-sm leading-tight">{school.name}</h4>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {school.address?.city}, {school.location?.voivodeship}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Stats grid */}
                        <div className="grid grid-cols-2 gap-2">
                            <div className="p-2 rounded-lg bg-muted/50 text-center">
                                <p className="text-lg font-bold text-primary">
                                    {school.stats?.totalStudents || '—'}
                                </p>
                                <p className="text-[10px] text-muted-foreground">uczniów w szkole</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50 text-center">
                                <p className="text-lg font-bold text-accent-green">
                                    {school.members_count || 0}
                                </p>
                                <p className="text-[10px] text-muted-foreground">na Klasowisku</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50 text-center">
                                <p className="text-lg font-bold text-accent-yellow">
                                    {school.stats?.classCount || '—'}
                                </p>
                                <p className="text-[10px] text-muted-foreground">klas</p>
                            </div>
                            <div className="p-2 rounded-lg bg-muted/50 text-center">
                                <p className="text-lg font-bold text-primary">
                                    {school.stats?.teacherCount || '—'}
                                </p>
                                <p className="text-[10px] text-muted-foreground">nauczycieli</p>
                            </div>
                        </div>

                        {/* Info */}
                        <div className="space-y-2 text-xs">
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Typ:</span>
                                <Badge variant="secondary">{school.typeCategory || school.type}</Badge>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-muted-foreground">Rodzaj:</span>
                                <span>{school.isPublic ? 'Publiczna' : 'Niepubliczna'}</span>
                            </div>
                            {school.address?.street && (
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">Adres:</span>
                                    <span className="text-right">
                                        {school.address.street} {school.address.number}
                                    </span>
                                </div>
                            )}
                            {school.website && (
                                <div className="flex items-center justify-between">
                                    <span className="text-muted-foreground">WWW:</span>
                                    <a 
                                        href={school.website} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline truncate max-w-[150px]"
                                    >
                                        {school.website}
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        {school.profile?.description && (
                            <div className="p-2 rounded-lg bg-muted/30">
                                <p className="text-xs text-muted-foreground line-clamp-3">
                                    {school.profile.description}
                                </p>
                            </div>
                        )}

                        {/* Action button */}
                        <Button 
                            className="w-full nk-button-green text-xs h-9"
                            onClick={() => navigate(`/school/${school.slug || school.id}`)}
                        >
                            <GraduationCap className="w-4 h-4 mr-2" />
                            Zobacz pełny profil szkoły
                        </Button>
                    </div>
                ) : null}
            </>
        );
    };

    return (
        <div className="nk-box overflow-hidden">
            <div className="nk-box-header flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {view === 'map' && 'Mapa szkół w Polsce'}
                {view === 'voivodeship' && 'Miasta'}
                {view === 'city' && 'Szkoły'}
                {view === 'school' && 'Profil szkoły'}
            </div>
            <div className="p-3">
                {view === 'map' && renderMap()}
                {view === 'voivodeship' && renderVoivodeship()}
                {view === 'city' && renderCity()}
                {view === 'school' && renderSchool()}
            </div>
        </div>
    );
};
